import React, { useState, useRef, useId } from "react"

// Allowed file types for upload
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png']

// Format file size for display
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

// Validate file type (PDF, JPG, PNG only)
function validateFileType(file: File): { valid: boolean; error?: string } {
  const hasValidType = ALLOWED_TYPES.includes(file.type)
  const hasValidExtension = ALLOWED_EXTENSIONS.some(ext =>
    file.name.toLowerCase().endsWith(ext)
  )

  if (!hasValidType && !hasValidExtension) {
    return {
      valid: false,
      error: `${file.name}: Only PDF, JPG, and PNG files are supported`
    }
  }

  return { valid: true }
}

// Validate file size
function validateFileSize(file: File, maxSize?: number): { valid: boolean; error?: string } {
  if (!maxSize) return { valid: true }
  
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `${file.name}: File size (${formatFileSize(file.size)}) exceeds maximum allowed size (${formatFileSize(maxSize)})`
    }
  }

  return { valid: true }
}

interface FileUploadProps {
  prompt: string[]
  processUploadedFiles: (files: File[]) => Promise<void>
  onValidationError?: (error: string) => void
  maxSize?: number // Maximum file size in bytes
}
const FileUpload: React.FC<FileUploadProps> = ({ prompt, processUploadedFiles, onValidationError, maxSize }) => {

  const inputId = useId()
  // Drag and drop handlers
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = useRef(0)

  const handleUploadDocument = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList || fileList.length === 0) return

    // Convert FileList to array and validate
    const files = Array.from(fileList)
    const validFiles: File[] = []

    for (const file of files) {
      const typeValidation = validateFileType(file)
      const sizeValidation = validateFileSize(file, maxSize)
      
      if (typeValidation.valid && sizeValidation.valid) {
        validFiles.push(file)
      } else {
        const error = typeValidation.error || sizeValidation.error
        if (error && onValidationError) {
          onValidationError(error)
        }
      }
    }

    if (validFiles.length > 0) {
      await processUploadedFiles(validFiles)
    }

    // Reset input so same files can be uploaded again
    e.target.value = ''
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setIsDragging(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    dragCounter.current = 0

    const fileList = e.dataTransfer.files
    if (!fileList || fileList.length === 0) return

    // Convert FileList to array and validate
    const files = Array.from(fileList)
    const validFiles: File[] = []

    for (const file of files) {
      const typeValidation = validateFileType(file)
      const sizeValidation = validateFileSize(file, maxSize)
      
      if (typeValidation.valid && sizeValidation.valid) {
        validFiles.push(file)
      } else {
        const error = typeValidation.error || sizeValidation.error
        if (error && onValidationError) {
          onValidationError(error)
        }
      }
    }

    if (validFiles.length > 0) {
      await processUploadedFiles(validFiles)
    }
  }

  return (
    <div
      className="flex-1 flex items-center justify-center p-0"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="text-center max-w-lg">
        {prompt.map((line, idx) => <div key={idx}>{line}</div>)}

        <div className={`
          border-2 border-dashed rounded-lg p-12 transition-all duration-200
          ${isDragging
            ? 'border-orange-500 bg-orange-50 scale-105'
            : 'border-gray-500 hover:border-orange-400 bg-white'
          }
        `}>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            multiple
            onChange={handleUploadDocument}
            className="hidden"
            id={inputId}
          />
          <label
            htmlFor={inputId}
            className="cursor-pointer flex flex-col items-center gap-6"
          >
            <div className={`
              w-20 h-20 rounded-full flex items-center justify-center transition-all duration-200
              ${isDragging
                ? 'bg-orange-200 scale-110'
                : 'bg-orange-100'
              }
            `}>
              <svg className="w-10 h-10 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <div>
              <div className="text-xl font-semibold text-gray-900">
                {isDragging ? 'Drop Files Here' : 'Upload Documents'}
              </div>
              <div className="text-gray-500 mt-1">
                {isDragging 
                  ? 'Release to upload' 
                  : `PDF, JPG, PNG • Multiple files • Drag & drop or click${maxSize ? ` • Max ${formatFileSize(maxSize)}` : ''}`
                }
              </div>
            </div>
          </label>
        </div>
      </div>
    </div>
  )

}

export default FileUpload